<script setup lang="ts">
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';

const { getBaseUrl } = useUrlHelper();

const paragraphClass = 'text-justify mb-3';
const titleClass = 'text-2xl font-semibold text-center text-blue-600 pb-5';
const subtitleClass = 'text-xl font-semibold text-blue-600 py-4';
</script>
<template>
  <div class="flex w-full justify-center bg-white flex-1 -mx-6 px-10">
    <div class="flex justify-center max-w-4xl flex-col py-16 text-sm">
      <div :class="titleClass">
        Health Insurance Application for Small Businesses
      </div>

      <p :class="paragraphClass">Looking for <a class="underline" :href="getBaseUrl('/group-health-insurance')" target="_blank">group health insurance plans for your small business?</a> Health for
      California makes it easy to sift through your options and find the right plan for
      your business and employees. Simply share some basic information about
      your business, and in less than a minute, you'll receive the best quotes from
      multiple insurance providers.</p>
      <p :class="paragraphClass">Fill in the form above to get started! You can explore your options with no
      obligation to buy.</p>

      <div :class="subtitleClass">
        Are You Eligible for Small Businesses Health Insurance?
      </div>

      <p :class="paragraphClass">Offering health insurance is an excellent way for a small business owner to
      show commitment to their employees' well-being. Yet, it can be difficult to
      navigate this process while balancing business demands. HFC works with
      Covered California to help small businesses provide employees with
      affordable health insurance.</p>
      <p :class="paragraphClass"><a class="underline" :href="getBaseUrl('/covered-ca-small-business')" target="_blank">Covered California for Small Business</a> (CCSB) is the state-run version of the
      Small Business Health Options Program (SHOP) from HealthCare.gov. This
      marketplace acts as a hub where employers can explore plan information,
      including pricing and benefits, then enroll their employees and consolidate
      billing.</p>

      <p :class="paragraphClass">You may be eligible for Covered California for Small Business if:</p>
      <ul class="list-disc list-inside mb-3">
        <li>Your business is located in California.</li>
        <li>You have between one and 99 W-2 employees.</li>
        <li>A majority of your eligible employees live in California.</li>
      </ul>

      <div :class="subtitleClass">
        Finding the Right Small Business Health Insurance
      </div>

      <p :class="paragraphClass">If your business is eligible for small business health insurance, Health for
      California is here to ensure you find the right options. We understand that
      your employees have different needs when it comes to health insurance. Some
      people rarely visit the doctor, while others may have serious medical
      conditions and use health services more regularly.</p>

      <p :class="paragraphClass">HFC makes it easy to compare the various group plans and browse the options
      that best meet your employees' needs. We'll help streamline the selection
      process and find ways to minimize employer costs. When it's time to choose
      the plans that work for you, you'll receive one bill and write a single check for
      all your employees.</p>

      <p :class="paragraphClass">Receive quotes from leading insurance companies, including Blue Shield of
      California, Kaiser Permanente and Sharp Health Plan. Plans are categorized
      into metallic tiers that determine cost distribution between the insured and
      the insurer — Bronze, Silver, Gold and Platinum. With CCSB, you can choose
      the level of coverage and various plan options, including PPO and HMO.</p>

      <div :class="subtitleClass">
        Understanding the Enrollment Process for Small Group Health Insurance
      </div>

      <p :class="paragraphClass">As your plan options appear, you’ll see rates and benefits from many of the
      different carriers in California offering small group health insurance. These
      quotes include:</p>
      <ul class="list-disc list-inside mb-3">
        <li>Carrier name</li>
        <li>Plan network type, such as PPO or HMO</li>
        <li>Metallic Tier</li>
        <li>Price of the plan</li>
        <li>Contribution Level</li>
        <li>Summary of Benefits</li>
        <li>And more!</li>
      </ul>

      <p :class="paragraphClass">Once you select a plan option and decide to enroll, an HFC group health
      insurance agent can guide you through this process. You will be provided with
      all application forms and <a class="underline" :href="getBaseUrl('/group-health-insurance/what-you-need-to-apply')" target="_blank">asked to provide the following information</a> to
      streamline enrollment:</p>
      <ul class="list-disc list-inside mb-3">
        <li>Federal Employer Identification Number (FEIN)</li>
        <li>State Employer Identification Number (SEIN)</li>
        <li>Copy of your DE-9C wage report</li>
        <li>Business license or Statement of Information</li>
        <li>Eligible employee information</li>
        <li>Additional business documentation as needed</li>
      </ul>

      <div :class="subtitleClass">
        Benefits of Group Health Insurance for Your Small Business
      </div>

      <p :class="paragraphClass">Offering group health insurance can <a class="underline" :href="getBaseUrl('/blog/small-business-health-insurance-guide')" target="_blank">benefit your small business in numerous ways</a>:</p>
      <ul class="list-disc list-inside mb-3">
        <li><b>Tax credits:</b> You may be eligible for a Small Business Health Care Tax
        Credit, which can help offset the cost of premiums by up to 50%.</li>
        <li><b>Tax deductions:</b> Your investment in the health of your employees may
        be considered a tax-deductible business expense.</li>
        <li><b>Employee attraction:</b> Offering health benefits helps attract and retain
      the best talent.</li>
        <li><b>Healthier workforce:</b> Keep your employees healthier and more
      productive by promoting preventive care and regular check-ups.</li>
      </ul>

      <div :class="subtitleClass">
        Learn More About Small Business Health Insurance at HFC
      </div>

      <p :class="paragraphClass">Let Health for California help you find the best rates for small business health
      insurance — quickly and conveniently. Fill out the form above to get your free
      quote or <a class="underline" :href="getBaseUrl('/contact-us')" target="_blank">reach out if you have any questions</a>.</p>
    </div>
  </div>
</template>
